/**
 * Shared enumerations — DESIGN.md §5, §6.10, §11.1.
 *
 * These are the vocabulary of the system. Values that the design describes as
 * *data* (statuses, tags, custom fields) deliberately do NOT live here — they
 * are admin-editable rows (§1.2 "Lookups are data, not code"). Only values the
 * code genuinely branches on are enumerated.
 */
/** Roles — §9.2. `can()` is the only place that should branch on these. */
export const USER_ROLES = ['member', 'admin'];
/** Status category — the reporting rollup that survives status renames (§6.6). */
export const STATUS_CATEGORIES = ['todo', 'in_progress', 'done'];
/** Who performed a mutation — §6.10, §12.5. */
export const ACTOR_TYPES = ['user', 'system', 'automation', 'agent'];
/** Card priority — §6.2. Null is a legitimate value ("no priority"). */
export const PRIORITIES = ['low', 'medium', 'high', 'urgent'];
/** Custom field types — §6.7. */
export const FIELD_TYPES = [
    'text',
    'number',
    'date',
    'checkbox',
    'select',
    'multi_select',
    'user',
    /**
     * Several people in one field — card #85, for Asana's Tester and Code
     * Reviewer columns. Stored as an array of user ids, exactly as
     * `multi_select` stores option ids, which is why it needed no migration:
     * `fields.type` is a text column and `customFieldValuesSchema` already
     * admitted `string[]` (its comment already said "option ids / user ids").
     *
     * Deliberately a field rather than a first-class role. As a field each
     * project configures its own (#27) and it is reversible; as a role it could
     * drive automations — "when Testing Outcome is Fail, notify the Tester" —
     * which is the better feature and a much larger change. That trade is
     * written up on #85 and this is the half that answers the question asked.
     */
    'multi_user',
    'url',
];
/** Lookup/definition scope — §6.7, §6.15. */
export const SCOPES = ['global', 'project'];
/**
 * View renderings — §7.1. Each is a renderer over one query engine.
 *
 * `table` is #80: the same cards and the same `columns`, laid out so they line
 * up and can be read *down*. It is a separate type rather than a mode of
 * `list` because the two disagree about the empty cell — the list omits a
 * field a card has nothing to say for (#50), and a table has to keep it or the
 * alignment is gone. `views.type` is plain text, so no migration.
 */
export const VIEW_TYPES = ['list', 'kanban', 'calendar', 'table'];
export const VIEW_SCOPES = ['personal', 'shared'];
/** Project lifecycle — §6.1. */
export const PROJECT_STATUSES = ['active', 'archived'];
/** Project health for status updates — §6.14. */
export const PROJECT_HEALTHS = ['on_track', 'at_risk', 'off_track'];
/** Why a user is a collaborator — §6.12. Drives "you're following because…". */
export const COLLABORATOR_REASONS = [
    'creator',
    'assignee',
    'mention',
    'commenter',
    'manual',
];
/** How a time entry was created — §6.13, D14. */
export const TIME_SOURCES = ['manual', 'timer', 'agent'];
/** Token scopes — §12.5. `admin` is deliberately absent and never grantable. */
export const TOKEN_SCOPES = ['read', 'write'];
/** Notification kinds — §11.1. `muted_kinds` filters on these. */
export const NOTIFICATION_KINDS = [
    'mention',
    'assignment',
    'comment',
    'status_change',
    'field_change',
    'attachment',
    'dependency',
    'completion',
    'project_status',
    'due_soon',
    /**
     * #77 — you have been asked to vote, or a poll you were in has closed.
     *
     * Its own kind rather than `field_change`, so someone who has muted field
     * changes on a busy card is still told when their sign-off is wanted. It is
     * also what lets the digest list polls separately from cards, which the card
     * asks for.
     */
    'poll',
    /**
     * #99 — a reminder you set on a card, or your default one, has come due.
     * Its own kind so it can email at once even in digest mode, and because
     * muting it means something different from muting `due_soon`'s morning list.
     */
    'reminder',
];
/** Email cadence — §11.2. Default is hourly_digest per R23. */
export const EMAIL_MODES = ['instant', 'hourly_digest', 'daily_digest', 'off'];
/**
 * Audit event catalog — §6.10.
 *
 * Append-only in spirit as well as storage: adding an event type is fine,
 * renaming one breaks historical reporting. The payload shape for each is
 * declared in `schemas/activity.ts`.
 */
export const EVENT_TYPES = [
    // Cards
    'task.created',
    'task.field_changed',
    'task.completed',
    'task.uncompleted',
    'task.deleted',
    'task.restored',
    'task.project_added',
    'task.project_removed',
    'task.section_moved',
    'task.subtask_added',
    'task.subtask_removed',
    'task.marked_duplicate',
    'task.unmarked_duplicate',
    /** #39 — recorded on the card that was cited, so the backlink has a moment. */
    'task.referenced',
    'task.recurrence_spawned',
    // Relationships
    'dependency.added',
    'dependency.removed',
    'collaborator.added',
    'collaborator.removed',
    // Discussion & media
    'comment.added',
    'comment.edited',
    'comment.deleted',
    'attachment.added',
    'attachment.deleted',
    // Time (§6.13 — audited, never notifying)
    'time.logged',
    'time.updated',
    'time.deleted',
    'timer.started',
    // Projects
    'project.created',
    'project.updated',
    'project.archived',
    'project.unarchived',
    'project.deleted',
    /** #9 — who may see a private project changed. */
    'project.access_changed',
    'project.status_posted',
    'section.created',
    'section.updated',
    'section.deleted',
    /**
     * §11.1's daily due-date sweep. Recorded against the *user*, not the cards:
     * one event per person per morning carrying the whole list, so a card that
     * stays overdue for a fortnight does not put a fortnight of reminders in its
     * own feed — and so the "one combined morning email" §11.1 asks for has one
     * thing to describe.
     */
    'notification.due_soon',
    /**
     * #99 — a personal reminder came due. Recorded against the *user*, like
     * `notification.due_soon`, and never against the card: a reminder is private
     * to the person who set it, and the card's feed is read by everyone on it.
     * The notification it produces still carries the card, so the Inbox links
     * to it and a card the person can no longer see drops out (#65).
     */
    'reminder.fired',
    /**
     * Sessions (§9.1, §17). Added for the audit surface: every *mutation* was
     * already an event, but signing in changes nothing, so nothing recorded it —
     * and "who logged in, and when" is the first question asked of an audit log.
     *
     * Failures are recorded only against an account that exists. There is no
     * uuid to attach an attempt on an unknown address to, and inventing one
     * would put attacker-supplied strings in the entity column; those attempts
     * are counted by the rate limiter instead (§17's no-enumeration rule keeps
     * them indistinguishable to the client either way).
     */
    'user.signed_in',
    'user.sign_in_failed',
    'user.signed_out',
    // Admin: people
    'user.created',
    'user.updated',
    'user.deactivated',
    'user.reactivated',
    'user.invited',
    'user.role_changed',
    'group.created',
    'group.updated',
    'group.deleted',
    'group.members_changed',
    // Admin: lookups
    'status.created',
    'status.updated',
    'status.archived',
    'tag.created',
    'tag.updated',
    'tag.archived',
    'field.created',
    'field.updated',
    'field.archived',
    'template.created',
    'template.updated',
    'template.archived',
    // Views
    'view.created',
    'view.updated',
    'view.shared',
    'view.unshared',
    'view.deleted',
    // Tokens & automations
    'token.created',
    'token.revoked',
    'automation.created',
    'automation.updated',
    'automation.deleted',
    'automation.executed',
    /**
     * Polls — card #77.
     *
     * Deliberately no event for a vote being *cast*. The activity feed is on the
     * card and every collaborator reads it, so "Ben voted" would announce the
     * turnout to people who have not voted yet, and "Ben voted Defer to later"
     * would hand out the results the card explicitly says to withhold. The votes
     * are their own rows with their own timestamps; the feed records the poll
     * being made, changed and closed, which is what a reader of the card needs.
     */
    'poll.created',
    'poll.updated',
    'poll.closed',
];
/** Entity types an activity event can be attached to — §5 `activity_events`. */
export const ENTITY_TYPES = [
    'task',
    'project',
    'section',
    'user',
    'group',
    'view',
    'status',
    'tag',
    'field',
    'template',
    'token',
    'automation',
    'comment',
    'attachment',
    'time_entry',
    /** #77 — a poll, which hangs off a card but is audited in its own right. */
    'poll',
];
//# sourceMappingURL=enums.js.map